import { AppointmentType, ProviderScheduleEntryKind } from 'constants/'
import { addDays } from 'date-fns'
import { useDeliveryModel } from 'hooks'
import { useMemo } from 'react'
import { useInfiniteQuery } from 'react-query'
import { Customer, ProviderScheduleEntry, QueryOptions } from 'types'
import { indexBy, logFactory, prop, toAwaitingAccess, toScheduleEntries, uniq } from 'utils'
import { useAppointmentsService, useCustomersService } from '../services'

type UseHookProps = {
  dateStart: Date
  resourceGid?: string
  types: AppointmentType[]
}

const WINDOW_IN_DAYS = 14

type SchedulePage = { entries: ProviderScheduleEntry[]; windowIndex: number }

const getNextWindowIndex = (lastPage: SchedulePage, pages: SchedulePage[]) => {
  const previousPage = pages.at(-2)
  const twoEmptyWindowsInARow = lastPage.entries.length === 0 && previousPage?.entries.length === 0

  return twoEmptyWindowsInARow ? undefined : lastPage.windowIndex + 1
}

const hookName = 'useProviderSchedule'
const log = logFactory(hookName)

const useProviderSchedule = ({ dateStart, resourceGid, types }: UseHookProps, options?: QueryOptions) => {
  const { getProviderSchedule } = useAppointmentsService()
  const { searchCustomers } = useCustomersService()
  const { deliveryModel } = useDeliveryModel()

  const enabled = Boolean(resourceGid) && (options?.enabled ?? true)

  const doQuery = async ({ pageParam = 0 }) => {
    const windowStart = addDays(dateStart, WINDOW_IN_DAYS * pageParam)
    const windowEnd = addDays(dateStart, WINDOW_IN_DAYS * (pageParam + 1))

    log(`🕧📆 Fetching provider schedule...`)

    const response = await getProviderSchedule({
      dateEnd: windowEnd,
      dateStart: windowStart,
      resourceGid: resourceGid as string,
      types,
    })
    const entries = (response?.data ?? []).filter((entry) => new Date(entry.start_date) < windowEnd)

    const customerSalesforceIds = uniq(
      entries.flatMap((entry) =>
        entry.kind === ProviderScheduleEntryKind.APPOINTMENT && entry.appointment.customer?.gid
          ? [entry.appointment.customer.gid]
          : []
      )
    )
    let customers: Customer[] = []

    if (customerSalesforceIds.length > 0) {
      log(`📆👤 Fetching schedule customers...`)
      const { data: rawCustomers = [] } = await searchCustomers({ salesforceId: customerSalesforceIds })

      log(`📆👤 Found ${rawCustomers.length} schedule customers`)
      customers = [...rawCustomers]
    }

    log(`🕧📆 Found ${entries.length} provider schedule entries`)

    return { customers, entries, windowIndex: pageParam }
  }

  const { data, fetchNextPage, hasNextPage, isError, isFetched, isFetchingNextPage, isLoading, refetch } =
    useInfiniteQuery([hookName, resourceGid, dateStart.toISOString(), types, deliveryModel], doQuery, {
      getNextPageParam: getNextWindowIndex,
      ...options,
      enabled,
    })

  const { awaitingAccess, scheduleEntries } = useMemo(() => {
    const pages = data?.pages ?? []
    const entries: ProviderScheduleEntry[] = pages.flatMap((page) => page.entries)
    const customersBySalesforceId = indexBy(
      pages.flatMap((page) => page.customers),
      prop('salesforceId')
    )

    return {
      awaitingAccess: toAwaitingAccess(entries, customersBySalesforceId),
      scheduleEntries: toScheduleEntries(entries, customersBySalesforceId),
    }
  }, [data])

  return {
    awaitingAccess,
    data: scheduleEntries,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetched,
    isFetchingNextPage,
    isLoading,
    refetch,
  }
}

export { hookName as providerScheduleQueryKey, useProviderSchedule }
